import React from 'react';
import ModalAction from '../components/modals/ModalAction';
import { getShareUrl } from '../utils/utils';
import { trackKlaviyoEvent, pushDataLayerEvent } from '../../utils/utils';
import { addLeadScore, getLeadScore, LEAD_SCORE_WEIGHTS } from '../utils/leadScoring';
import { getToken, persistPortalToken } from '../utils/auth';


/**
 * @param {Object} opts
 * @param {(content: React.ReactNode) => void} opts.showModal
 * @param {string} opts.orderId
 * @param {(action: 'approved' | 'updates-requested' | 'declined' | 'requestUpdates' | 'requestExtension' | 'askQuestion') => void} opts.onComplete
 */
export function useDraftOrderActionModals({ showModal, orderId, onComplete, canPayByPo = false, quoteName = '', customerEmail = '' }) {

  const pushModalOpenDataLayer = (eventName) => {
    if (!orderId) return;
    pushDataLayerEvent(eventName, {
      order_id: orderId,
      customer_email: customerEmail || undefined,
      quote_name: quoteName || undefined,
    });
  };


  const handleApprove = () => {
    pushModalOpenDataLayer('quote_portal_approve_modal_open');
    showModal(
      <ModalAction
        mode="approve"
        orderId={orderId}
        title="Approve Quote"
        quoteName={quoteName}
        customerEmail={customerEmail}
        canPayByPo={canPayByPo}
        className="approve-quote-modal"
        // onSuccess={(action) => {
        onSuccess={(action, result) => {
          if (result?.token) persistPortalToken(result.token);
          const converted = result?.converted !== false;

          let scoreDelta = 0;
          let scoreFlagKey = '';
          if (action === 'approved') {
            scoreDelta = converted
              ? LEAD_SCORE_WEIGHTS.approvedConverted
              : LEAD_SCORE_WEIGHTS.approvedNotConverted;
            scoreFlagKey = converted ? 'approved_converted' : 'approved_not_converted';
          } else if (action === 'updates-requested') {
            scoreDelta = LEAD_SCORE_WEIGHTS.updatesRequested;
            scoreFlagKey = 'updates_requested';
          }

          const leadScore =
            scoreDelta !== 0
              ? addLeadScore(orderId, scoreDelta, scoreFlagKey)
              : getLeadScore(orderId);

          // GA tracking
          pushDataLayerEvent('quote_portal_approve', {
            orderId: orderId,
            action,
            converted,
            customer_email: customerEmail || undefined,
            quote_name: quoteName || undefined,
            lead_score: leadScore,
            lead_score_delta: scoreDelta,
          });

          

          if (action === 'approved') {
            const converted = result?.converted !== false;
            showModal(
              <ModalAction
                mode="success"
                title="Quote Approved!"
                // message="Your quote has been approved and converted to an order. You should receive a confirmation email shortly."
                message={converted
                  ? "Your quote has been approved and converted to an order. You should receive a confirmation email shortly."
                  : "Your approval has been received. Our team will review and convert this quote to an order shortly."}
              />
            );
            trackKlaviyoEvent('Quote Portal Approved', {
              DraftOrderId: orderId,
              QuoteID: quoteName || undefined,
              Converted: converted ? 1 : 0,
              Action: action,
              customer_email: customerEmail || undefined,
              LeadScore: leadScore,
              LeadScoreDelta: scoreDelta,
            });
          } else if (action === 'updates-requested') {
            showModal(
              <ModalAction
                mode="success"
                title="Updates Requested"
                message="Your update request has been submitted. Our team will review and get back to you shortly."
              />
            );
            trackKlaviyoEvent('Quote Portal Updates Requested', {
              DraftOrderId: orderId,
              QuoteID: quoteName || undefined,
              customer_email: customerEmail || undefined,
              LeadScore: leadScore,
              LeadScoreDelta: scoreDelta,
            });
          } else {
            trackKlaviyoEvent('Quote Portal Approved', {
              DraftOrderId: orderId,
              QuoteID: quoteName || undefined,
              Converted: converted ? 1 : 0,
              Action: action,
              customer_email: customerEmail || undefined,
              LeadScore: leadScore,
              LeadScoreDelta: scoreDelta,
            });
          }
          onComplete?.(action);
        }}
      />
    );
  };

  const handleRequestUpdates = () => {
    pushModalOpenDataLayer('quote_portal_request_updates_modal_open');
    console.log('handleRequestUpdates');
    showModal(
      <ModalAction
        mode="requestUpdates"
        orderId={orderId}
        title="Request Updates"
        quoteName={quoteName}
        customerEmail={customerEmail}
        className="request-updates-modal"
        onSuccess={() => {
          const scoreDelta = LEAD_SCORE_WEIGHTS.updatesRequested;
          const leadScore = addLeadScore(orderId, scoreDelta, 'updates_requested');

          pushDataLayerEvent('quote_portal_request_updates', {
            order_id: orderId,
            customer_email: customerEmail || undefined,
            quote_name: quoteName || undefined,
            lead_score: leadScore,
            lead_score_delta: scoreDelta,
          });

          // Klaviyo tracking (scoring)
          trackKlaviyoEvent('Quote Portal Updates Requested', {
            DraftOrderId: orderId,
            QuoteID: quoteName || undefined,
            customer_email: customerEmail || undefined,
            LeadScore: leadScore,
            LeadScoreDelta: scoreDelta,
          });

          showModal(
            <ModalAction
              mode="success"
              title="Updates Requested"
              message="Your update request has been submitted. Our team will review and get back to you shortly."
            />
          );
          onComplete?.('requestUpdates');
        }}
      />
    );
  };

  const handleDecline = () => {
    pushModalOpenDataLayer('quote_portal_decline_modal_open');
    showModal(
      <ModalAction
        mode="decline"
        orderId={orderId}
        title="Decline Quote"
        quoteName={quoteName}
        customerEmail={customerEmail}
        className="decline-quote-modal"
        onSuccess={() => {
          const scoreDelta = LEAD_SCORE_WEIGHTS.declined;
          const leadScore = addLeadScore(orderId, scoreDelta, 'declined');

          pushDataLayerEvent('quote_portal_decline', {
            order_id: orderId,
            customer_email: customerEmail || undefined,
            quote_name: quoteName || undefined,
            lead_score: leadScore,
            lead_score_delta: scoreDelta,
          });

          const { token } = getToken();
          const quoteUrl = getShareUrl(orderId, token, quoteName);

          // Klaviyo tracking (scoring)
          trackKlaviyoEvent('Quote Portal Declined', {
            DraftOrderId: orderId,
            QuoteID: quoteName || undefined,
            customer_email: customerEmail || undefined,
            QuoteURL: quoteUrl || window.location.href || undefined,
            LeadScore: leadScore,
            LeadScoreDelta: scoreDelta,
          });

          showModal(
            <ModalAction
              mode="success"
              title="Quote Declined"
              message="Your quote has been declined. Thank you for letting us know."
            />
          );
          onComplete?.('declined');
        }}
        onSecondaryAction={handleRequestUpdates}
      />
    );
  };

  const handleRequestExtension = () => {
    pushModalOpenDataLayer('quote_portal_request_extension_modal_open');
    showModal(
      <ModalAction
        mode="requestExtension"
        orderId={orderId}
        title="Request Extension"
        className="request-extension-modal"
        onSuccess={() => {
          const scoreDelta = LEAD_SCORE_WEIGHTS.extensionRequested;
          const leadScore = addLeadScore(orderId, scoreDelta, 'extension_requested');

          pushDataLayerEvent('quote_portal_request_extension', {
            order_id: orderId,
            customer_email: customerEmail || undefined,
            quote_name: quoteName || undefined,
            lead_score: leadScore,
            lead_score_delta: scoreDelta,
          });

          // Klaviyo tracking (scoring)
          trackKlaviyoEvent('Quote Portal Extension Requested', {
            DraftOrderId: orderId,
            QuoteID: quoteName || undefined,
            customer_email: customerEmail || undefined,
            LeadScore: leadScore,
            LeadScoreDelta: scoreDelta,
          });

          showModal(
            <ModalAction
              mode="success"
              title="Extension Requested"
              message="Your extension request has been submitted. Our team will review and get back to you shortly."
            />
          );
          onComplete?.('requestExtension');
        }}
      />
    );
  };

  const handleAskQuestion = () => {
    pushModalOpenDataLayer('quote_portal_ask_question_modal_open');
    showModal(
      <ModalAction
        mode="askQuestion"
        orderId={orderId}
        title="Have a Question?"
        className="ask-question-modal"
        onSuccess={() => {
          const scoreDelta = LEAD_SCORE_WEIGHTS.questionAsked;
          const leadScore = addLeadScore(orderId, scoreDelta, 'question_asked');

          pushDataLayerEvent('quote_portal_ask_question', {
            order_id: orderId,
            customer_email: customerEmail || undefined,
            quote_name: quoteName || undefined,
            lead_score: leadScore,
            lead_score_delta: scoreDelta,
          });

          // Klaviyo tracking (scoring)
          trackKlaviyoEvent('Quote Portal Question Asked', {
            DraftOrderId: orderId,
            QuoteID: quoteName || undefined,
            customer_email: customerEmail || undefined,
            LeadScore: leadScore,
            LeadScoreDelta: scoreDelta,
          });
          showModal(
            <ModalAction
              mode="success"
              title="Question Submitted"
              message="Your question has been submitted. Our team will review and get back to you shortly."
            />
          );
          onComplete?.('askQuestion');
        }}
      />
    );

    
  };

  return { handleApprove, handleDecline, handleRequestUpdates, handleRequestExtension, handleAskQuestion };
}
